import { useCallback, useEffect, useRef, useState, type PointerEvent, type MouseEvent, type RefObject, type WheelEvent } from 'react';

// Horizontal drag distance (px) past which a swipe steps one category.
const SWIPE_THRESHOLD_PX = 120;
// Accumulated horizontal wheel/trackpad delta before stepping one category.
const WHEEL_STEP_PX = 120;
// Idle gap after which the gesture snaps to the nearest item.
const SNAP_RELEASE_MS = 150;

interface MobileSwipe {
  swipeHandlers: {
    onPointerDown: (event: PointerEvent) => void;
    onPointerMove: (event: PointerEvent) => void;
    onPointerUp: (event: PointerEvent) => void;
    onPointerCancel: (event: PointerEvent) => void;
    onClickCapture: (event: MouseEvent) => void;
  };
  onWheel: (event: WheelEvent) => void;
  scrollProgress: number; // Signed -1..1 continuous progress to next/prev tab
}

export function useChatteaser3MobileSwipe({
  activeTab,
  tabCount,
  onSelect,
}: {
  activeTab: number;
  tabCount: number;
  onSelect: (index: number) => void;
}): MobileSwipe {
  const [scrollProgress, setScrollProgress] = useState(0);
  const localTabRef = useRef(activeTab);

  // Sync localTabRef with the external activeTab whenever it changes from outside
  useEffect(() => {
    localTabRef.current = activeTab;
  }, [activeTab]);

  const startRef = useRef<{ x: number; y: number } | null>(null);
  const axisRef = useRef<'x' | 'y' | null>(null);
  const isDraggingRef = useRef(false);
  const accumRef = useRef(0);
  const wheelAccumRef = useRef(0);
  const snapTimerRef = useRef<number | null>(null);

  const clearSnap = useCallback((): void => {
    if (snapTimerRef.current == null) return;
    window.clearTimeout(snapTimerRef.current);
    snapTimerRef.current = null;
  }, []);

  const reset = useCallback((): void => {
    accumRef.current = 0;
    setScrollProgress(0);
  }, []);

  const onRelease = useCallback((): void => {
    const accum = accumRef.current;
    reset();
    if (accum >= 0.5 && localTabRef.current < tabCount - 1) {
      localTabRef.current += 1;
      onSelect(localTabRef.current);
    } else if (accum <= -0.5 && localTabRef.current > 0) {
      localTabRef.current -= 1;
      onSelect(localTabRef.current);
    }
  }, [onSelect, reset, tabCount]);

  const updateProgress = useCallback((rawDelta: number, threshold: number, shiftStart?: (amount: number) => void): void => {
    let next = rawDelta;
    if (localTabRef.current >= tabCount - 1 && next > 0) next = 0;
    if (localTabRef.current <= 0 && next < 0) next = 0;

    const progress = next / threshold;

    if (progress >= 1) {
      clearSnap();
      reset();
      if (shiftStart) shiftStart(threshold);
      localTabRef.current += 1;
      onSelect(localTabRef.current);
      return;
    }
    if (progress <= -1) {
      clearSnap();
      reset();
      if (shiftStart) shiftStart(-threshold);
      localTabRef.current -= 1;
      onSelect(localTabRef.current);
      return;
    }

    accumRef.current = progress;
    setScrollProgress(progress);
    clearSnap();
    snapTimerRef.current = window.setTimeout(onRelease, SNAP_RELEASE_MS);
  }, [clearSnap, onRelease, onSelect, reset, tabCount]);

  const onPointerDown = useCallback((event: PointerEvent): void => {
    startRef.current = { x: event.clientX, y: event.clientY };
    axisRef.current = null;
    isDraggingRef.current = false;
    clearSnap();
  }, [clearSnap]);

  const onPointerMove = useCallback((event: PointerEvent): void => {
    if (!startRef.current) return;
    const dx = startRef.current.x - event.clientX; 
    const dy = startRef.current.y - event.clientY;

    if (!axisRef.current) {
      if (Math.abs(dx) > 5 && Math.abs(dx) > Math.abs(dy)) {
        axisRef.current = 'x';
      } else if (Math.abs(dy) > 5) {
        axisRef.current = 'y';
      }
    }

    if (axisRef.current === 'x') {
      isDraggingRef.current = true;
      updateProgress(dx, SWIPE_THRESHOLD_PX, (shift) => {
        if (startRef.current) startRef.current.x -= shift;
      });
    } else if (axisRef.current === 'y') {
      // Intentionally do nothing and let the browser native scroll take over.
      // Do not mark isDraggingRef.current = true so clicks aren't eaten.
    }
  }, [updateProgress]);

  const onPointerUp = useCallback(
    (event: PointerEvent): void => {
      startRef.current = null;
      axisRef.current = null;
      onRelease();
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 0);
    },
    [onRelease],
  );

  const onPointerCancel = useCallback((event: PointerEvent): void => {
    startRef.current = null;
    axisRef.current = null;
    reset();
    isDraggingRef.current = false;
  }, [reset]);

  const onClickCapture = useCallback((event: MouseEvent): void => {
    if (isDraggingRef.current) {
      event.stopPropagation();
      event.preventDefault();
    }
  }, []);

  const onWheel = useCallback(
    (event: WheelEvent): void => {
      // Only horizontal intent drives the tabs; vertical falls through to the
      // card's own scroller.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      // Accumulate the gesture and PREVIEW the slide on the tab strip, but commit
      // exactly once when it settles. Committing mid-scroll fires rapid onSelect
      // calls whose overlapping card Exit/Enter animations leave the card in a
      // broken, half-transitioned (shifted) state — so we debounce to a single
      // change, and a fast flick lands in one jump rather than a burst.
      let pixels = wheelAccumRef.current + event.deltaX;
      const maxForward = (tabCount - 1 - localTabRef.current) * WHEEL_STEP_PX;
      const maxBackward = -localTabRef.current * WHEEL_STEP_PX;
      pixels = Math.max(maxBackward, Math.min(maxForward, pixels));
      // Cap a single gesture to one adjacent tab: a multi-step jump commits a
      // non-adjacent index, which stresses the card's exit→enter transition.
      pixels = Math.max(-WHEEL_STEP_PX, Math.min(WHEEL_STEP_PX, pixels));
      wheelAccumRef.current = pixels;
      setScrollProgress(pixels / WHEEL_STEP_PX);

      clearSnap();
      snapTimerRef.current = window.setTimeout(() => {
        const steps = Math.round(wheelAccumRef.current / WHEEL_STEP_PX);
        wheelAccumRef.current = 0;
        setScrollProgress(0);
        const target = localTabRef.current + steps;
        if (target !== localTabRef.current) {
          localTabRef.current = target;
          onSelect(target);
        }
      }, SNAP_RELEASE_MS);
    },
    [clearSnap, onSelect, tabCount],
  );

  useEffect(() => {
    return () => {
      clearSnap();
    };
  }, [clearSnap]);

  return {
    swipeHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture },
    onWheel,
    scrollProgress,
  };
}
